import { useMemo, useEffect } from 'react';
import { useTranslation } from 'react-i18next';
import { useSelector, useDispatch } from 'react-redux';
import {
  reportFilterCountryCodeSelector,
  reportFilterThirdPartySalesByItemsGroupBySelector,
  reportThirdPartySalesByItemsSelector,
  downloadReportPdf,
} from '../../state';
import { ThirdPartySalesByItems } from '../../types';
import { DataGridFlexSales, useDataGridFlexSales, useDataGridSync } from '../DataGridFlexSales';
import handlerFactory, { ThirdPartySalesByItemsDataGridContext } from './handlerFactory';

const DataGridConfig = () => {
  const { t } = useTranslation();
  const { handler } = useDataGridFlexSales<
    ThirdPartySalesByItems,
    ThirdPartySalesByItemsDataGridContext
  >();
  const country = useSelector(reportFilterCountryCodeSelector);
  const { loading, data } = useSelector(reportThirdPartySalesByItemsSelector);
  const groupBy = useSelector(reportFilterThirdPartySalesByItemsGroupBySelector);

  const gridContext: ThirdPartySalesByItemsDataGridContext = useMemo(
    () => ({
      t,
      country,
      groupBy,
    }),
    [t, country, groupBy]
  );

  useDataGridSync({
    handler,
    data,
    context: gridContext,
    title: t('thirdPartySalesByItems.title'),
    loading,
  });

  return null;
};

const DataGridExportConfig = () => {
  const { handler } = useDataGridFlexSales<
    ThirdPartySalesByItems,
    ThirdPartySalesByItemsDataGridContext
  >();
  const { t } = useTranslation();
  const dispatch = useDispatch();

  useEffect(() => {
    handler.setExportConfig({
      PDF: {
        generator: (table, title) => {
          dispatch(
            downloadReportPdf({
              locale: handler.locale,
              t,
              table,
              title,
              includeMissingClosingTable: false,
              parameters: [
                'countryCode',
                'stores',
                'pos',
                'salesTypes',
                'segments',
                'timeBand',
                'hourRange',
                'startAccountingDate',
                'endAccountingDate',
                'startOperationDate',
                'endOperationDate',
                'startBusinessDate',
                'endBusinessDate',
                'groupBy',
              ],
              opStructures: [
                'regionalMgmt',
                'region',
                'management',
                'supervision',
                'agreement',
                'company',
              ],
            })
          );
        },
        isDispatchAction: true,
      },
    });
  }, [handler, dispatch, t]);

  return null;
};

const ThirdPartySalesByItemsDataGrid = () => (
  <DataGridFlexSales handlerFactory={handlerFactory}>
    <DataGridConfig />
    <DataGridExportConfig />
  </DataGridFlexSales>
);

export default ThirdPartySalesByItemsDataGrid;
